import { useEffect, useState } from 'react';
import { createApiGateway } from '../apiGateway';
import { notification } from 'antd';

const apiGateway = createApiGateway();

export type Ssp = {
  id: number;
  name: string;
  partnerId?: number;
  dailySpendLimit?: number;
  totalSpendLimit?: number;
  isActive: boolean;
  swapperEnableSwapper: boolean | null;
  swapperSspId: number | null;
  swapperBidFloorStep: number | null;
  swapperMaxAttempts: number | null;
  swapperRetryDelay: number | null;
  swapperStartDelay: number | null;
  overpayEnabled: boolean | null;
  overpayBudget: number | null;
  overpayDailyBudget: number | null;
  overpayPricePercentage: number | null;
  maxOverpay: number | null;
  optimizer?: boolean | number;
  company?: User | null;
  lastHourStatistics?: {
    sspBidRequest: number;
  };
  dailySpend?: number;
  totalSpend?: number;
};

type User = {
  id: number;
  firstName?: string;
};

type UseSspsOptions = {
  loadLastHourStats?: boolean;
  loadSpend?: boolean;
};

export function useSsps({ loadLastHourStats = true, loadSpend = true }: UseSspsOptions = {}) {
  const [ssps, setSsps] = useState<Ssp[]>([]);
  const [isBaseLoaded, setIsBaseLoaded] = useState(false);
  const [isLastHourStatsLoaded, setIsLastHourStatsLoaded] = useState(false);
  const [isSpendLoaded, setIsSpendLoaded] = useState(false);

  useEffect(() => {
    let isMounted = true;

    (async () => {
      try {
        const [sspsResponse, usersResponse] = await Promise.all([
          apiGateway.ssps.getAll({ includeSpend: false }),
          apiGateway.users.getAll(),
        ]);

        if (!isMounted) {
          return;
        }

        const sspsToSet = sspsResponse.map((ssp: Ssp) => {
          const company = usersResponse.find((u: User) => u.id === ssp.partnerId) || null;

          return {
            ...ssp,
            company,
          };
        });

        setSsps(sspsToSet);
        setIsBaseLoaded(true);
      } catch (error) {
        if (isMounted) {
          setIsBaseLoaded(true);
        }
      }

      if (loadLastHourStats) {
        try {
          const lastHourStatisticsResponse = await apiGateway.statisticsV2.getLastHourSspStatistics();

          if (!isMounted) {
            return;
          }

          if (lastHourStatisticsResponse.rows.length) {
            notification.info({
              title: `Statistics loaded for ${lastHourStatisticsResponse.hour} UTC`,
              duration: 1,
            });
          } else {
            notification.info({
              title: 'No SSP statistics were found for the recent hourly window',
              duration: 1,
            });
          }

          setSsps((prevSsps) =>
            prevSsps.map((ssp) => ({
              ...ssp,
              lastHourStatistics: lastHourStatisticsResponse.rows.find((x) => String(x.sspId).includes(`[${ssp.id}]`)),
            }))
          );
        } catch (error) {
          // Ignore statistics errors and keep base list available.
        } finally {
          if (isMounted) {
            setIsLastHourStatsLoaded(true);
          }
        }
      } else if (isMounted) {
        setIsLastHourStatsLoaded(true);
      }

      if (loadSpend) {
        try {
          const sspsWithSpend: Array<{ id: number; dailySpend?: number; totalSpend?: number }> =
            await apiGateway.ssps.getAll({ includeSpend: true });

          if (!isMounted) {
            return;
          }

          const spendById = new Map(
            sspsWithSpend.map((ssp) => [ssp.id, { dailySpend: ssp.dailySpend, totalSpend: ssp.totalSpend }])
          );

          setSsps((prevSsps) =>
            prevSsps.map((ssp) => ({
              ...ssp,
              dailySpend: spendById.get(ssp.id)?.dailySpend ?? 0,
              totalSpend: spendById.get(ssp.id)?.totalSpend ?? 0,
            }))
          );
        } catch (error) {
          // Ignore spend errors and keep base list available.
        } finally {
          if (isMounted) {
            setIsSpendLoaded(true);
          }
        }
      } else if (isMounted) {
        setIsSpendLoaded(true);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [loadLastHourStats, loadSpend]);

  return { ssps, isBaseLoaded, isLastHourStatsLoaded, isSpendLoaded };
}
